<template>
  <div class="container admin-table-page">
    <div class="param-title-wrap niif-header">
      <div class="d-flex align-items-center w-100">
        <div class="d-flex align-items-center">
          <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
            <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
          </b-button>
          <h2 class="param-title">{{ labels.title }}</h2>
        </div>
      </div>
    </div>

    <div class="param-title-wrap niif-header">
      <div class="d-flex align-items-center w-100">
        <div class="filters-container d-flex align-items-center">
          <!-- Filtro Compañía -->
          <b-form-select
            id="company-select"
            v-model="companyId"
            @change="onCompanyChange"
            class="filter-select"
          >
            <b-form-select-option value="">
              {{ labels.compania }}...
            </b-form-select-option>
            <option v-for="company in listCompany" :key="company?.id" :value="company?.id">
              {{ company.acronym + "-" + company?.nombre }}
            </option>
          </b-form-select>

          <!-- Filtro Tipo (Capital/Resultados/Valorizacion) -->
          <b-form-select
            id="tipo-select"
            v-model="tipoCuenta"
            @change="onTipoChange"
            class="filter-select ml-2"
            :disabled="!companyId"
          >
            <b-form-select-option value="">
              {{ labels.selectTipo }}
            </b-form-select-option>
            <b-form-select-option value="1">
              {{ labels.capital }}
            </b-form-select-option>
            <b-form-select-option value="2">
              {{ labels.resultados }}
            </b-form-select-option>
            <b-form-select-option value="3">
              {{ labels.valorizacion }}
            </b-form-select-option>
          </b-form-select>

          <!-- Filtro SubTipo (Cuentas/Tasas) -->
          <b-form-select
            id="subtipo-select"
            v-model="subTipo"
            @change="onSubTipoChange"
            class="filter-select ml-2"
            :disabled="!companyId || !tipoCuenta"
          >
            <b-form-select-option value="">
              {{ labels.selectSubTipo }}
            </b-form-select-option>
            <b-form-select-option value="cuentas">
              {{ labels.cuentas }}
            </b-form-select-option>
            <b-form-select-option value="tasas">
              {{ labels.tasas }}
            </b-form-select-option>
          </b-form-select>

          <!-- Selector de Moneda (solo para Tasas) -->
          <b-form-select
            v-if="subTipo === 'tasas'"
            id="currency-select"
            v-model="idCurrency"
            @change="onCurrencyChange"
            class="filter-select ml-2"
            :disabled="!companyId || !tipoCuenta"
          >
            <b-form-select-option value="">
              {{ labels.conversion }}
            </b-form-select-option>
            <option v-for="currency in listMonedas" :key="currency?.id" :value="currency?.id">
              {{ currency?.nombre }}
            </option>
          </b-form-select>

          <!-- Botón Consultar -->
          <b-button
            class="consult-btn ml-2"
            @click="consultar()"
            :disabled="!isFormValid"
          >
            <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
            {{ labels.consultar }}
          </b-button>
        </div>
      </div>
    </div>

    <div class="admin-table-block mt-4">
      <!-- ESTADO INICIAL - Sin datos -->
      <div v-if="!fgl_consulta && !isLoading && items.length === 0">
        <CorporateDataTable
          :items="[]"
          :fields="[]"
          :labels="tableLabels"
          :loading="false"
          :default-per-page="10"
          :page-options="[10]"
          :narrow="false"
          :table-min-height="400"
          :table-max-height="500"
          :show-search="false"
          :allow-column-toggle="false"
          :enable-sorting="false"
          :show-pagination="false"
          :show-rows-per-page="false"
        >
          <template #empty-state>
            <div class="text-center py-5">
              <font-awesome-icon
                :icon="['fas', 'search']"
                size="3x"
                class="text-muted mb-3"
                style="opacity: 0.3;"
              />
              <p class="h4 text-muted">
                {{ labels.selectFilters }}
              </p>
            </div>
          </template>
        </CorporateDataTable>
      </div>

      <!-- LOADING -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner></b-spinner>
        <p class="mt-2">{{ labels.loading }}</p>
      </div>

      <!-- TABLA CON DATOS - Cuentas -->
      <div v-if="!isLoading && fgl_consulta && subTipo === 'cuentas' && items.length > 0">
        <CorporateDataTable
          :items="items"
          :fields="cuentasFields"
          :labels="tableLabels"
          :default-per-page="10"
          :page-options="[10, 20, 50, 100]"
          :narrow="false"
          :table-min-height="500"
          :table-max-height="500"
          :show-search="showSearchTable"
          :allow-column-toggle="true"
          :enable-sorting="true"
          :filter-defs="cuentasFilterDefs"
          :toolbar-disabled="!showToolbarExtras"
        >
          <template #toolbar-actions>
            <div class="niif-toolbar-right">
              <b-button
                size="sm"
                class="table-toolbar-add-btn"
                @click="f_crea_cuentas()"
                :disabled="!showToolbarExtras || isVigenciaBloqueada || !companyId || !tipoCuenta"
              >
                {{ labels.create }}
              </b-button>
            </div>
          </template>

          <template #cell(cuenta)="data">
            <div class="text-center"><strong>{{ data.item?.cuenta }}</strong></div>
          </template>

          <template #cell(descripcion)="data">
            <div class="text-left">{{ data.item?.descripcion }}</div>
          </template>

          <template #cell(vigencia)="data">
            <div class="text-center">{{ data.item?.vigencia }}</div>
          </template>

          <template #cell(tasa_conversion)="data">
            <div class="text-right">{{ data.item?.tasa_conversion }}</div>
          </template>

          <template #cell(valor_local)="data">
            <div class="text-right">{{ data.item?.valor_local }}</div>
          </template>

          <template #cell(valor_funcional)="data">
            <div class="text-right">{{ data.item?.valor_funcional }}</div>
          </template>

          <template #cell(accionEdit)="data">
            <b-button
              style="background-color: #FF924C; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="f_muestra_cuenta(data.item)"
              :disabled="isVigenciaBloqueada"
            >
              <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
            </b-button>
          </template>

          <template #cell(accionDelete)="data">
            <b-button
              class="reset-btn-danger table-action-btn"
              style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
              @click.stop="deleteCuenta(data.item.id)"
              :disabled="isVigenciaBloqueada"
            >
              <i><font-awesome-icon :icon="['fas', 'trash']" size="xs"/></i>
            </b-button>
          </template>
        </CorporateDataTable>
      </div>

      <!-- TABLA CON DATOS - Tasas -->
      <div v-if="!isLoading && fgl_consulta && subTipo === 'tasas' && items.length > 0">
        <CorporateDataTable
          :items="items"
          :fields="tasasFields"
          :labels="tableLabels"
          :default-per-page="10"
          :page-options="[10, 20, 50, 100]"
          :narrow="false"
          :table-min-height="500"
          :table-max-height="500"
          :show-search="showSearchTable"
          :allow-column-toggle="true"
          :enable-sorting="true"
          :filter-defs="tasasFilterDefs"
          :toolbar-disabled="!showToolbarExtras"
        >
          <template #toolbar-actions>
            <div class="niif-toolbar-right">
              <b-button
                size="sm"
                class="table-toolbar-add-btn"
                @click="f_crea_tasas()"
                :disabled="!showToolbarExtras || isVigenciaBloqueada || !companyId || !tipoCuenta || !idCurrency"
              >
                {{ labels.create }}
              </b-button>
            </div>
          </template>

          <template #cell(vigencia)="data">
            <div class="text-center">{{ data.item?.vigencia }}</div>
          </template>

          <template #cell(tasa_cierre)="data">
            <div class="text-right">{{ data.item?.tasa_cierre }}</div>
          </template>

          <template #cell(inverso)="data">
            <div class="text-center">{{ data.item?.inverso === 1 ? 'Verdadero' : 'Falso' }}</div>
          </template>

          <template #cell(accionEdit)="data">
            <b-button
              style="background-color: #FF924C; width: 30px; height: 30px;"
              class="md-fab md-primary table-action-btn"
              @click.stop="f_muestra(data.item)"
              :disabled="isVigenciaBloqueada"
            >
              <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
            </b-button>
          </template>
        </CorporateDataTable>
      </div>

      <!-- TABLA VACÍA - Consulta realizada sin resultados -->
      <div v-if="!isLoading && fgl_consulta && items.length === 0" class="text-center py-5">
        <font-awesome-icon
          :icon="['fas', 'table']"
          size="3x"
          class="text-muted mb-3"
          style="opacity: 0.3;"
        />
        <p class="h4 text-muted">
          {{ labels.noData }}
        </p>
        <p class="text-muted">
          {{ labels.noResults }}
        </p>
      </div>
    </div>

    <!-- Modal Crear Cuentas -->
    <b-modal
      v-model="creacuentas"
      :no-close-on-backdrop="true"
      hide-footer
            centered
      size="lg"
      :title="labels.createCuenta + ' - ' + titulo_grupo"
      header-class="reset-modal-header"
      title-class="reset-modal-title"
      body-class="reset-modal-body"
      class="reset-password-dialog edit-niif-dialog"
     @hide="closeModal">
      <div class="uniform-modal-body">
        <div class="password-policy-box mb-4">
          <div class="password-policy-title">{{ labels.infoTitleCuenta }}</div>
          <div class="password-policy-subtitle">{{ labels.infoTextCuenta }}</div>
        </div>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.criterio_cuenta }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="criterioC"
              @input="f_criterio_cuenta"
              type="text"
              class="form-input"
              :placeholder="labels.criterio_cuenta"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.criterio_descripcion }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="criterioD"
              @input="f_criterio_descripcion"
              type="text"
              class="form-input"
              :placeholder="labels.criterio_descripcion"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.cuenta }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-select
              v-model="cuenta"
              class="form-input"
            >
              <b-form-select-option :value="null">
                -- {{ labels.select }} --
              </b-form-select-option>
              <b-form-select-option
                v-for="item in showCuentas"
                :value="item?.cuenta"
                :key="item?.id"
              >
                {{ item?.cuenta }}-{{ item?.descripcion }}
              </b-form-select-option>
            </b-form-select>
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.valor }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="valor"
              type="number"
              class="form-input"
              :placeholder="labels.valor"
              @blur="f_funcional()"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.vigencia }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="vigencia"
              type="month"
              class="form-input"
              :placeholder="labels.vigencia"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.tasa }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="tasa"
              type="number"
              class="form-input"
              :placeholder="labels.tasa"
              @blur="f_funcional()"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.valor_funcional }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="valor_funcional"
              type="number"
              class="form-input"
              :placeholder="labels.valor_funcional"
            />
          </b-col>
        </b-row>

        <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
          <b-button
            v-if="id"
            class="reset-btn-danger mr-2"
            @click="grabeCuentas(true)"
          >
            {{ labels.eliminar }}
          </b-button>
          <b-button
            class="reset-btn-secondary mr-2"
            @click="creacuentas = false"
          >
            {{ labels.cerrar }}
          </b-button>
          <b-button
            class="reset-btn-primary"
            @click="grabeCuentas(false)"
          >
            {{ labels.guardar }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Crear Tasas -->
    <b-modal
      v-model="creatasas"
      :no-close-on-backdrop="true"
      hide-footer
            centered
      size="lg"
      :title="labels.createTasa + ' - ' + titulo_grupo"
      header-class="reset-modal-header"
      title-class="reset-modal-title"
      body-class="reset-modal-body"
      class="reset-password-dialog edit-niif-dialog"
     @hide="closeModal">
      <div class="uniform-modal-body">
        <div class="password-policy-box mb-4">
          <div class="password-policy-title">{{ labels.infoTitleTasa }}</div>
          <div class="password-policy-subtitle">{{ labels.infoTextTasa }}</div>
        </div>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.validity }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formEdit.vigencia"
              type="month"
              class="form-input"
              :placeholder="labels.validity"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.currency }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formEdit.nombre_moneda"
              type="text"
              class="form-input"
              disabled
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.tasaPatrimonio }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formEdit.tasa_cierre"
              type="number"
              class="form-input"
              :placeholder="labels.tasaPatrimonio"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.inverso }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-select
              v-model="formEdit.inverso"
              class="form-input"
            >
              <b-form-select-option :value="0">Falso</b-form-select-option>
              <b-form-select-option :value="1">Verdadero</b-form-select-option>
            </b-form-select>
          </b-col>
        </b-row>

        <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
          <b-button
            class="reset-btn-secondary mr-2"
            @click="creatasas = false"
          >
            {{ labels.cerrar }}
          </b-button>
          <b-button
            class="reset-btn-primary"
            @click="grabeTasaPatrimonio()"
          >
            {{ labels.guardar }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Editar Tasas -->
    <b-modal
      v-model="showDialogEdit"
      :no-close-on-backdrop="true"
      hide-footer
            centered
      size="lg"
      :title="labels.modificar + ' - ' + titulo_grupo"
      header-class="reset-modal-header"
      title-class="reset-modal-title"
      body-class="reset-modal-body"
      class="reset-password-dialog edit-niif-dialog"
     @hide="closeModal">
      <div class="uniform-modal-body">
        <div class="password-policy-box mb-4">
          <div class="password-policy-title">{{ labels.infoTitleEdit }}</div>
          <div class="password-policy-subtitle">{{ labels.infoTextEdit }}</div>
        </div>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.validity }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formEdit.vigencia"
              type="month"
              class="form-input"
              disabled
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.currency }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formEdit.nombre_moneda"
              type="text"
              class="form-input"
              disabled
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.closingRate }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-input
              v-model="formEdit.tasa_cierre"
              type="number"
              class="form-input"
              :placeholder="labels.closingRate"
            />
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center uniform-form-row">
          <b-col cols="12" md="4" class="text-left uniform-label-col">
            <label class="form-label">{{ labels.inverso }}</label>
          </b-col>
          <b-col cols="12" md="8" class="text-left">
            <b-form-select
              v-model="formEdit.inverso"
              class="form-input"
            >
              <b-form-select-option :value="0">Falso</b-form-select-option>
              <b-form-select-option :value="1">Verdadero</b-form-select-option>
            </b-form-select>
          </b-col>
        </b-row>

        <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
          <b-button
            class="reset-btn-secondary mr-2"
            @click="showDialogEdit = false"
          >
            {{ labels.cerrar }}
          </b-button>
          <b-button
            class="reset-btn-primary"
            @click="modificaTasaPatrimonio()"
          >
            {{ labels.modificar }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal Confirmar Eliminación -->
    <InacticaItem
      :InactivaItem="showModDel"
      @confirmDelete="confirmDel"
      @cierraModal="closeModal"
    />
  </div>
</template>

<script>
import Company from "@/services/Company";
import TasaCambio from "@/services/TasaCambio";
import Currency from "@/services/Currency";
import InacticaItem from '../utils/InacticaItem.vue'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
  name: "TasasPatrimonio",
  components: {
    InacticaItem,
    CorporateDataTable
  },
  data() {
    return {
      // Estado de consulta
      fgl_consulta: false,
      isLoading: false,

      // Filtros principales
      companyId: "",
      tipoCuenta: "",
      subTipo: "",
      idCurrency: "",
      idMoneda: "",

      // Datos
      listCompany: [],
      listCurrency: [],
      listMonedas: [],
      items: [],
      titulo_grupo: "",

      // Estado de UI
      showDialogEdit: false,
      showModDel: false,
      rowDelete: null,

      // Formulario Tasas
      formEdit: {
        vigencia: "",
        tasa_cierre: "",
        inverso: 0,
        nombre_moneda: "",
        id_conversion: "",
        idCompania: "",
        tipo_cuenta: "",
        userId: "",
        id: "",
      },

      // Cuentas
      creacuentas: false,
      creatasas: false,
      cuentasPatrimonio: [],
      showCuentas: [],
      listCuentas: [],
      cuenta: null,
      criterioC: "",
      criterioD: "",
      valor: 0,
      tasa: "",
      valor_funcional: 0,
      vigencia: "",
      id: null,
      elimina: false,

      // Validación de período
      vigenciaAnterior: "",
      formEditVigenciaAnterior: "",

      error: false,
    };
  },
  computed: {
    isVigenciaBloqueada() {
      if (this.items && this.items.length > 0) {
        const vigencia = this.items[0]?.vigencia;
        if (vigencia) {
          return !this.$store.getters.isPeriodoEnabled(vigencia);
        }
      }
      return false;
    },

    isFormValid() {
      if (!this.companyId) return false;
      if (!this.tipoCuenta) return false;
      if (!this.subTipo) return false;
      if (this.subTipo === 'tasas' && !this.idCurrency) return false;
      return true;
    },

    showToolbarExtras() {
      return this.fgl_consulta && this.items.length > 0;
    },

    showSearchTable() {
      return this.fgl_consulta && this.items.length > 0;
    },

    labels() {
      if (this.$store.state._language == "es-ES") {
        return {
          title: "Tasas de cambio - Patrimonio",
          cuentas: "Cuentas",
          cuenta: "Cuenta",
          descripcion: "Descripción",
          tasas: "Tasas",
          capital: "Capital",
          resultados: "Resultados",
          valorizacion: "Valorización",
          regresar: "Regresar",
          compania: "Compañía",
          nameTasa: "Tasa cambio",
          validity: "Vigencia",
          closingRate: "Tasa cierre",
          tasaPatrimonio: "Tasa Patrimonio",
          inverso: "Inverso",
          averageRate: "Tasa promedio",
          internalRate: "Tasa interna",
          currency: "Moneda",
          conversion: "Moneda de conversión",
          modificar: "Modificación de Tasas",
          criterio_cuenta: "Criterio Cuenta",
          criterio_descripcion: "Criterio Descripción",
          valor: "Valor Moneda",
          valor_funcional: "Valor Moneda Funcional",
          vigencia: "Fecha",
          tasa: "Tasa",
          selectTipo: "Seleccione Tipo",
          selectSubTipo: "Seleccione Opción",
          consultar: "Consultar",
          create: "Crear",
          createCuenta: "Crear Cuenta",
          createTasa: "Crear Tasa",
          guardar: "Guardar",
          cerrar: "Cerrar",
          eliminar: "Eliminar",
          edit: "Editar",
          delete: "Eliminar",
          noData: "No hay datos disponibles",
          noResults: "No se encontraron resultados",
          loading: "Cargando...",
          selectFilters: "Seleccione los filtros y consulte",
          searchPlaceholder: "Buscar en la tabla...",
          rowsPerPage: "Filas por página:",
          noSort: "Sin orden",
          sortAsc: "A-Z / Menor a mayor",
          sortDesc: "Z-A / Mayor a menor",
          columns: "Columnas",
          showAllColumns: "Mostrar todas",
          filters: "Filtros",
          clearFilters: "Limpiar filtros",
          noMatches: "No se encontraron resultados",
          filterCuenta: "Filtrar cuenta...",
          filterDescripcion: "Filtrar descripción...",
          filterVigencia: "Filtrar vigencia...",
          select: "Seleccione...",
          infoTitleCuenta: "Datos de la Cuenta",
          infoTextCuenta: "Complete la información de la cuenta y guarde los cambios.",
          infoTitleTasa: "Datos de la Tasa",
          infoTextTasa: "Complete la información de la tasa y guarde los cambios.",
          infoTitleEdit: "Editar Tasa",
          infoTextEdit: "Actualice la información de la tasa y guarde los cambios.",
        };
      } else {
        return {
          title: "Heritage Exchange Rate",
          cuentas: "Account",
          cuenta: "Account",
          descripcion: "Description",
          tasas: "Rates",
          create: "Create",
          capital: "Capital",
          resultados: "Results",
          valorizacion: "Valorization",
          regresar: "Return",
          compania: "Company",
          nameTasa: "Exchange rate",
          validity: "Validity",
          closingRate: "Closing rate",
          tasaPatrimonio: "Heritage rate",
          inverso: "Invers",
          averageRate: "Average rate",
          internalRate: "Internal rate",
          currency: "Currency",
          conversion: "Exchange Currency",
          modificar: "Exchange Rate Update",
          criterio_cuenta: "Account Criteria",
          criterio_descripcion: "Description Criteria",
          valor: "Currency Value",
          valor_funcional: "Functional Currency Value",
          vigencia: "Date",
          tasa: "Rate",
          selectTipo: "Select Type",
          selectSubTipo: "Select Option",
          consultar: "Consult",
          createCuenta: "Create Account",
          createTasa: "Create Rate",
          guardar: "Save",
          cerrar: "Close",
          eliminar: "Delete",
          edit: "Edit",
          delete: "Delete",
          noData: "No data available",
          noResults: "No results found",
          loading: "Loading...",
          selectFilters: "Select filters and consult",
          searchPlaceholder: "Search in table...",
          rowsPerPage: "Rows per page:",
          noSort: "No sorting",
          sortAsc: "A-Z / Low to high",
          sortDesc: "Z-A / High to low",
          columns: "Columns",
          showAllColumns: "Show all",
          filters: "Filters",
          clearFilters: "Clear filters",
          noMatches: "No results found",
          filterCuenta: "Filter account...",
          filterDescripcion: "Filter description...",
          filterVigencia: "Filter validity...",
          select: "Select...",
          infoTitleCuenta: "Account Data",
          infoTextCuenta: "Complete the account information and save changes.",
          infoTitleTasa: "Rate Data",
          infoTextTasa: "Complete the rate information and save changes.",
          infoTitleEdit: "Edit Rate",
          infoTextEdit: "Update the rate information and save changes.",
        };
      }
    },

    cuentasFields() {
      return [
        { key: 'cuenta', label: this.labels.cuenta, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
        { key: 'vigencia', label: this.labels.vigencia, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: 'tasa_conversion', label: this.labels.tasa, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'valor_local', label: this.labels.valor, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'valor_funcional', label: this.labels.valor_funcional, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
        { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
      ];
    },

    tasasFields() {
      return [
        { key: 'vigencia', label: this.labels.validity, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: 'tasa_cierre', label: this.labels.closingRate, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'inverso', label: this.labels.inverso, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
        { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
      ];
    },

    cuentasFilterDefs() {
      return [
        { key: 'cuenta', type: 'text', placeholder: this.labels.filterCuenta },
        { key: 'descripcion', type: 'text', placeholder: this.labels.filterDescripcion },
        { key: 'vigencia', type: 'text', placeholder: this.labels.filterVigencia }
      ];
    },

    tasasFilterDefs() {
      return [
        { key: 'vigencia', type: 'text', placeholder: this.labels.filterVigencia }
      ];
    },

    tableLabels() {
      return {
        noRow: this.labels.noData,
        noMatches: this.labels.noMatches,
        searchPlaceholder: this.labels.searchPlaceholder,
        rowsPerPage: this.labels.rowsPerPage,
        noSort: this.labels.noSort,
        sortAsc: this.labels.sortAsc,
        sortDesc: this.labels.sortDesc,
        columns: this.labels.columns,
        showAllColumns: this.labels.showAllColumns,
        filters: this.labels.filters,
        clearFilters: this.labels.clearFilters
      };
    }
  },

  watch: {
    vigencia(valNew) {
      if (valNew !== this.vigenciaAnterior) {
        this.vigenciaAnterior = valNew;
        this.alertaSiPeriodoBloqueado(valNew);
      }
    },
    'formEdit.vigencia'(valNew) {
      if (valNew !== this.formEditVigenciaAnterior) {
        this.formEditVigenciaAnterior = valNew;
        this.alertaSiPeriodoBloqueado(valNew);
      }
    }
  },

  methods: {
    goBack() {
      this.$router.back();
    },

    // Eventos de cambio de filtros
    onCompanyChange() {
      this.tipoCuenta = "";
      this.subTipo = "";
      this.idCurrency = "";
      this.items = [];
      this.fgl_consulta = false;
      this.titulo_grupo = "";
    },

    onTipoChange() {
      this.subTipo = "";
      this.idCurrency = "";
      this.items = [];
      this.fgl_consulta = false;
      this.updateTituloGrupo();
    },

    onSubTipoChange() {
      this.idCurrency = "";
      this.items = [];
      this.fgl_consulta = false;
      if (this.subTipo === 'tasas' && this.listMonedas.length === 0) {
        this.obtenerMonedas();
      }
    },

    onCurrencyChange() {
      this.items = [];
      this.fgl_consulta = false;
    },

    updateTituloGrupo() {
      const tipoMap = {
        1: "CAPITAL",
        2: this.$store.state._language == "es-ES" ? "RESULTADOS" : "RESULTS",
        3: this.$store.state._language == "es-ES" ? "VALORIZACION" : "VALORIZATION",
      };
      this.titulo_grupo = tipoMap[this.tipoCuenta] || "";
    },

    // Consulta principal
    consultar() {
      if (!this.isFormValid) {
        this.$toast.error(this.$store.state._language == "es-ES" ? "Complete todos los filtros" : "Complete all filters");
        return;
      }

      this.isLoading = true;
      this.fgl_consulta = true;

      if (this.subTipo === 'cuentas') {
        this.leaGrupoPatrimonio();
      } else if (this.subTipo === 'tasas') {
        this.leaGrupoPatrimonioTasas();
      }
    },

    // Cuentas
    leaGrupoPatrimonio() {
      TasaCambio.leaGrupoPatrimonio(this.companyId, this.tipoCuenta)
        .then((result) => {
          this.items = result.data.cuentasPatrimonio || [];
          if (this.items.length === 0) {
            this.$toast.info(this.$store.state._language == "es-ES" ? "No hay cuentas registradas" : "No accounts registered");
          }
        })
        .catch(() => {
          this.error = true;
          this.items = [];
          this.$toast.error(this.$store.state._language == "es-ES" ? "Error al consultar cuentas" : "Error consulting accounts");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Tasas
    leaGrupoPatrimonioTasas() {
      if (!this.companyId || !this.tipoCuenta || !this.idCurrency) {
        this.isLoading = false;
        return;
      }

      const datos = {
        companyId: this.companyId,
        tipo_cuenta: this.tipoCuenta,
        idOrigen: this.idMoneda,
        idConversion: this.idCurrency,
      };

      TasaCambio.leaGrupoPatrimonioTasas(datos)
        .then((result) => {
          this.items = result.data.tasaCambio || [];
          if (this.items.length === 0) {
            this.$toast.info(this.$store.state._language == "es-ES" ? "No hay tasas registradas" : "No rates registered");
          }
        })
        .catch(() => {
          this.error = true;
          this.items = [];
          this.$toast.error(this.$store.state._language == "es-ES" ? "Error al consultar tasas" : "Error consulting rates");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Métodos para Cuentas
    f_crea_cuentas() {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      this.criterioC = "";
      this.criterioD = "";
      this.creacuentas = true;
      this.id = null;
      this.valor = 0;
      this.tasa = "";
      this.vigencia = "";
      this.valor_funcional = 0;
      this.cuenta = null;

      TasaCambio.leaCuentasPatrimonio(this.companyId, this.tipoCuenta)
        .then((result) => {
          this.listCuentas = result.data.cuentasPatrimonio || [];
          this.showCuentas = this.listCuentas;
        })
        .catch(() => {
          this.error = true;
        });
    },

    f_crea_tasas() {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      if (!this.idCurrency) {
        this.$toast.warning(this.$store.state._language == "es-ES" ? 
          "Seleccione una moneda primero" : 
          "Select a currency first");
        return;
      }

      this.creatasas = true;
      this.formEdit = {
        vigencia: "",
        idCompania: this.companyId,
        tipo_cuenta: this.tipoCuenta,
        userId: this.$store.state.userId,
        id_conversion: this.idCurrency,
        nombre_moneda: this.getNameCurrency(this.idCurrency),
        inverso: 0,
        tasa_cierre: "",
        id: "",
      };
    },

    f_criterio_cuenta() {
      if (this.criterioC.length == 0) {
        this.showCuentas = this.listCuentas;
      } else {
        const regex = new RegExp(this.criterioC, "i");
        this.showCuentas = this.listCuentas.filter((cuenta) =>
          regex.test(cuenta.cuenta)
        );
      }
    },

    f_criterio_descripcion() {
      if (this.criterioD.length == 0) {
        this.showCuentas = this.listCuentas;
      } else {
        this.showCuentas = this.listCuentas.filter((cuenta) =>
          cuenta.descripcion.toUpperCase().includes(this.criterioD.toUpperCase())
        );
      }
    },

    f_muestra_cuenta(v_cuentas) {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      this.elimina = true;
      this.id = v_cuentas.id;
      this.cuenta = v_cuentas.cuenta;
      this.valor = v_cuentas.valor_local;
      this.tasa = v_cuentas.tasa_conversion;
      this.vigencia = v_cuentas.vigencia;
      this.valor_funcional = v_cuentas.valor_funcional;
      this.creacuentas = true;
    },

    grabeCuentas(eliminar) {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      const informacion = [
        this.companyId,
        this.tipoCuenta,
        this.cuenta,
        this.valor,
        this.vigencia,
        this.tasa,
        this.valor_funcional,
      ];

      if (!eliminar && informacion.includes(null) || informacion.includes("")) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "Campo(s) pendiente(s) por diligenciar" : 
          "Fields pending to fill");
        return;
      }

      let infoCuentas = {
        id: this.id,
        userId: this.$store.state.userId,
      };

      if (!eliminar) {
        infoCuentas.companyId = this.companyId;
        infoCuentas.tipo_cuenta = this.tipoCuenta;
        infoCuentas.cuenta = this.cuenta;
        const v_cuenta = this.showCuentas.find((item) => item.cuenta == this.cuenta);
        infoCuentas.descripcion = v_cuenta ? v_cuenta.descripcion : "";
        infoCuentas.valor = this.valor;
        infoCuentas.vigencia = this.vigencia;
        infoCuentas.tasa = this.tasa;
        infoCuentas.valor_funcional = this.valor_funcional;
      }

      this.creacuentas = false;

      TasaCambio.grabeCuentas(infoCuentas)
        .then((result) => {
          if (result.status === 200) {
            this.$toast.success(this.$store.state._language == "es-ES" ? 
              "Información Grabada" : 
              "Information Saved");
          } else {
            this.$toast.error(this.$store.state._language == "es-ES" ? 
              "Problemas al grabar. Consulte!" : 
              "Problems saving. Check!");
          }
        })
        .catch(() => {
          this.$toast.error(this.$store.state._language == "es-ES" ? 
            "Información no Grabada" : 
            "Information not saved");
          this.error = true;
        })
        .finally(() => {
          setTimeout(() => {
            this.consultar();
          }, 2000);
        });
    },

    deleteCuenta(id) {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para eliminar" : 
          "The selected validity is disabled for delete");
        return;
      }
      this.rowDelete = id;
      this.showModDel = true;
    },

    confirmDel() {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para eliminar" : 
          "The selected validity is disabled for delete");
        this.showModDel = false;
        return;
      }

      const infoCuentas = {
        id: this.rowDelete,
        userId: this.$store.state.userId,
        eliminar: true
      };

      TasaCambio.grabeCuentas(infoCuentas)
        .then((result) => {
          if (result.status === 200) {
            this.$toast.success(this.$store.state._language == "es-ES" ? 
              "Información Eliminada" : 
              "Information Deleted");
          } else {
            this.$toast.error(this.$store.state._language == "es-ES" ? 
              "Problemas al eliminar. Consulte!" : 
              "Problems deleting. Check!");
          }
        })
        .catch(() => {
          this.$toast.error(this.$store.state._language == "es-ES" ? 
            "Error al eliminar" : 
            "Error deleting");
          this.error = true;
        })
        .finally(() => {
          this.showModDel = false;
          setTimeout(() => {
            this.consultar();
          }, 2000);
        });
    },

    f_funcional() {
      if (this.tasa != 0 && this.tasa) {
        this.valor_funcional = parseFloat((this.valor / this.tasa).toFixed(2));
      }
    },

    // Métodos para Tasas
    f_muestra(r_tasas) {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      this.formEdit = { ...r_tasas };
      this.formEdit.nombre_moneda = this.getNameCurrency(r_tasas.id_conversion);
      this.showDialogEdit = true;
    },

    grabeTasaPatrimonio() {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      this.creatasas = false;

      if (!this.formEdit.vigencia) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "Falta Campo Vigencia" : 
          "Validity field missing");
        return;
      }

      const vl_vigencia = this.formEdit.vigencia.replace("-", "");
      const v_vigencia = this.items.find((obj) => obj.vigencia === vl_vigencia);
      if (v_vigencia) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "Vigencia ya definida. Modifiquela" : 
          "Validity already defined. Modify it");
        return;
      }

      if (!this.formEdit.tasa_cierre) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "Falta información de la tasa del Patrimonio" : 
          "Heritage rate information missing");
        return;
      }

      this.formEdit.userId = this.$store.state.userId;

      TasaCambio.grabeTasaPatrimonio(this.formEdit)
        .then((result) => {
          if (result.status === 200) {
            this.$toast.success(this.$store.state._language == "es-ES" ? 
              "Información Grabada" : 
              "Information Saved");
          } else {
            this.$toast.error(this.$store.state._language == "es-ES" ? 
              "Información no Grabada" : 
              "Information not saved");
          }
        })
        .catch(() => {
          this.$toast.error(this.$store.state._language == "es-ES" ? 
            "Información no Grabada" : 
            "Information not saved");
          this.error = true;
        })
        .finally(() => {
          setTimeout(() => {
            this.consultar();
          }, 2000);
        });
    },

    modificaTasaPatrimonio() {
      if (this.isVigenciaBloqueada) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
        return;
      }

      this.showDialogEdit = false;
      this.formEdit.userId = this.$store.state.userId;

      TasaCambio.modificaTasaPatrimonio(this.formEdit)
        .then((result) => {
          if (result.status === 200) {
            this.$toast.success(this.$store.state._language == "es-ES" ? 
              "Información Grabada" : 
              "Information Saved");
          } else {
            this.$toast.error(this.$store.state._language == "es-ES" ? 
              "Información no Grabada" : 
              "Information not saved");
          }
        })
        .catch(() => {
          this.$toast.error(this.$store.state._language == "es-ES" ? 
            "Información no Grabada" : 
            "Information not saved");
          this.error = true;
        })
        .finally(() => {
          setTimeout(() => {
            this.consultar();
          }, 2000);
        });
    },

    // Métodos auxiliares
    obtenerMonedas() {
      if (this.listCurrency.length === 0) {
        Currency.getAllCurrency()
          .then((result) => {
            this.listCurrency = result.data.currency || [];
            this.listMonedas = this.listCurrency;
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        this.listMonedas = this.listCurrency;
      }
    },

    getNameCurrency(id) {
      const objMoneda = this.listCurrency.find((obj) => obj.id === id);
      return objMoneda ? objMoneda.nombre : "";
    },

    getAcronymCurrency(id) {
      const objMoneda = this.listCurrency.find((obj) => obj.id === id);
      return objMoneda ? objMoneda.acronimo : "";
    },

    isPeriodoBloqueado(vigencia) {
      if (!vigencia) return false;
      return !this.$store.getters.isPeriodoEnabled(vigencia);
    },

    alertaSiPeriodoBloqueado(vigencia) {
      if (this.isPeriodoBloqueado(vigencia)) {
        this.$toast.error(this.$store.state._language == "es-ES" ? 
          "La vigencia seleccionada está deshabilitada para crear o editar" : 
          "The selected validity is disabled for create or edit");
      }
    },

    closeModal(value) {
      this.showModDel = value;
      if (!value) {
        this.rowDelete = null;
      }
    }
  },

  mounted() {
    this.isLoading = true;

    Promise.all([
      Company.getAllSubsidiarias(),
      Currency.getAllCurrency()
    ])
    .then(([companyRes, currencyRes]) => {
      this.listCompany = companyRes.data.companies || [];
      this.listCurrency = currencyRes.data.currency || [];
      this.listMonedas = this.listCurrency;
    })
    .catch((err) => {
      console.log(err);
      this.$toast.error(this.$store.state._language == "es-ES" ? 
        "Error al cargar los datos iniciales" : 
        "Error loading initial data");
    })
    .finally(() => {
      this.isLoading = false;
    });
  }
};
</script>

<style src="../main.css"></style>

<style scoped>
/* Solo estilos de layout, los modales usan clases globales */
.niif-header .filters-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 0 !important;
  flex-wrap: wrap;
}

.niif-header .filter-select {
  min-width: 180px;
  max-width: 250px;
  width: auto;
  height: 38px;
  font-size: 14px;
  border-radius: 7px;
}

.niif-header .filter-select option {
  white-space: normal;
  word-break: break-word;
  padding: 4px 8px;
}

.niif-header .consult-btn {
  height: 38px;
  min-height: 38px;
  padding: 0 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 7px;
  font-size: 14px;
  background-color: #6c757d !important;
  color: white !important;
  border: 1px solid #6c757d !important;
  cursor: pointer;
  flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
  background-color: #6c757d !important;
  color: white !important;
  border-color: #6c757d !important;
  box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  background-color: #6c757d !important;
  color: white !important;
  border-color: #6c757d !important;
}

.page-back-btn {
  padding: 0;
  font-size: 1.5rem;
  line-height: 1;
}

.back-icon {
  font-size: 1.75rem !important;
  width: 1.75rem !important;
  height: 1.75rem !important;
}

.param-title {
  font-size: 2rem !important;
  font-weight: 500;
}

.niif-toolbar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
}

.table-action-btn {
  border: none !important;
  border-radius: 4px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.table-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 1200px) {
  .niif-header .filter-select {
    min-width: 150px;
    max-width: 200px;
    height: 36px;
    font-size: 13px;
  }
  
  .niif-header .consult-btn {
    height: 36px;
    min-height: 36px;
    font-size: 13px;
    padding: 0 16px;
  }
}

@media (max-width: 992px) {
  .niif-header .filters-container {
    flex-wrap: wrap;
    width: 100%;
    margin-top: 8px;
    margin-left: 0 !important;
    justify-content: flex-start;
    gap: 6px;
  }
  
  .niif-header .filter-select {
    min-width: 140px;
    max-width: 180px;
    height: 36px;
    font-size: 13px;
    flex: 1 1 auto;
  }
  
  .niif-header .consult-btn {
    height: 36px;
    min-height: 36px;
    font-size: 13px;
    padding: 0 16px;
  }
}

@media (max-width: 576px) {
  .param-title {
    font-size: 1.5rem !important;
  }
  
  .niif-header .filter-select {
    min-width: 100%;
    max-width: 100%;
    height: 36px;
    font-size: 13px;
    flex: 0 0 100%;
  }
  
  .niif-header .filters-container {
    gap: 4px;
  }
  
  .niif-header .consult-btn {
    width: 100%;
    flex: 0 0 100%;
    height: 36px;
    margin-left: 0 !important;
    margin-top: 4px;
  }
}
</style>